import { MentionsMultiMap } from "@8x8/chalet-proto";
import {
    DisplayableDialog,
    DisplayableRoom,
    DisplayableUser,
    isInsideUserRoster,
    mergeRooms,
    RoomInfo,
    Rooms,
    sortRoster,
    updateRoster,
} from "chalet-shared";
import produce from "immer";
import * as I from "immutable";
import { createSelector } from "reselect";

import { Action, actionCreators } from "../actions";
import { Data, RoomGroup } from "../service/Data";
import { assign } from "../utils/any";
import { setConcat, shallowSameOrderedMap } from "../utils/immutable";

import { RootState } from ".";
import { getOpenRoomIds } from "./rooms";
import { getRosterSearchTerm } from "./rosterSearch";

export type roomId = string;

export type RosterView = RoomGroup | "settings";
export type SettingsView = "index" | "debug";

export interface Store {
    rooms: Rooms;
    searchResults: Rooms;
    monologRoomId: string | undefined;
    loadingByUserIds: I.Set<string>;
    loadingByRoomIds: I.Set<string>;
    inSortMoratorium: boolean;
    isIdle: boolean;
    isRosterLoaded: boolean;
    mentionsMultiMap: MentionsMultiMap; // FIXME: stateful object, ignored by redux dev tools
    activeRosterView: RosterView;
    activeSettingsView: SettingsView;
}

export function initialStore(): Store {
    return {
        rooms: I.OrderedMap(),
        searchResults: I.OrderedMap(),
        monologRoomId: undefined,
        loadingByUserIds: I.Set(),
        loadingByRoomIds: I.Set(),
        inSortMoratorium: false,
        isIdle: false,
        isRosterLoaded: false,
        mentionsMultiMap: new MentionsMultiMap(),
        activeRosterView: "all",
        activeSettingsView: "index",
    };
}

export function update(store = initialStore(), action: Action): Store {
    switch (action.type) {
        case actionCreators.onSync.type:
        case actionCreators.onLogout.type:
        case actionCreators.onWrongToken.type: {
            return initialStore();
        }
        case actionCreators.onActive.type: {
            return { ...store, isIdle: false };
        }
        case actionCreators.onIdle.type: {
            return { ...store, isIdle: true };
        }
        case actionCreators.doSortRoster.type: {
            const { respectSortMoratorium, joinedRooms } = action.payload;
            const { inSortMoratorium, rooms } = store;
            return {
                ...store,
                rooms: sortRoster({
                    respectSortMoratorium,
                    joinedRooms,
                    inSortMoratorium,
                    rooms,
                }),
            };
        }
        case actionCreators.rosterSortMoratoriumOn.type: {
            return { ...store, inSortMoratorium: true };
        }
        case actionCreators.rosterSortMoratoriumOff.type: {
            return { ...store, inSortMoratorium: false };
        }
        case actionCreators.rosterSearchOff.type: {
            if (store.searchResults.size) {
                return { ...store, searchResults: store.searchResults.clear() };
            }
            return store;
        }
        case actionCreators.onRosterSearchResults.type: {
            // CD:MEDIUM: we can lose data if results are coming out of order
            const { payload: data } = action;
            const sortedRooms = (data.rooms || []).sort((a, b) => {
                if (a.score && b.score) {
                    return b.score - a.score;
                } else {
                    return 0;
                }
            });
            const searchResults = I.OrderedMap(mergeRooms(sortedRooms));
            return { ...store, searchResults };
        }
        case actionCreators.onRoster.type: {
            const {
                payload: { data },
            } = action;
            const { rooms: rooms0, monologRoomId } = updateRoster(
                data,
                store.rooms,
                store.mentionsMultiMap
            );
            let rooms = rooms0;
            data.rooms &&
                data.rooms.forEach(({ id: roomId, mention_collisions }) => {
                    if (!mention_collisions) {
                        return;
                    }
                    const room = rooms.get(roomId);
                    if (!(room && room.name && room.userId)) {
                        return;
                    }
                    const { userId, name, userEmail, dialog_external_login_id } = room;

                    let displayName = name;
                    if (mention_collisions.includes("email")) {
                        displayName = `${name} (${dialog_external_login_id || "EXT_ID_NA"})`;
                    } else if (mention_collisions.includes("name")) {
                        displayName = `${name} (${userEmail || "EMAIL_NA"})`;
                    }

                    // XXX NOTE: this _only_ deletes the match, meaning this user will not be mentionable.
                    // *However* — this user's id is still going to pop up for each prefix, so we'll still see this entry in the mention selector.
                    // To fix, change MMM.Delete to go through substring in name.
                    store.mentionsMultiMap.DeleteMatch(name, userId);
                    store.mentionsMultiMap.Add(displayName, userId);

                    rooms = rooms.set(roomId, { ...room, display_name: displayName });
                });

            return {
                ...store,
                rooms,
                monologRoomId: assign(store.monologRoomId, monologRoomId),
                isRosterLoaded: true,
                // and side effects to mentionsMultiMap
            };
        }

        //
        case actionCreators.ensureUsersLoadedStarted.type: {
            let { loadingByUserIds } = store;
            loadingByUserIds = setConcat(loadingByUserIds, I.Set(action.payload.userIds));
            return { ...store, loadingByUserIds };
        }
        case actionCreators.ensureUsersLoadedSucceeded.type:
        case actionCreators.ensureUsersLoadedFailed.type: {
            let { loadingByUserIds } = store;
            loadingByUserIds = loadingByUserIds.subtract(action.payload.userIds);
            return { ...store, loadingByUserIds };
        }
        //
        case actionCreators.ensureRoomsLoadedStarted.type: {
            let { loadingByRoomIds } = store;
            loadingByRoomIds = setConcat(loadingByRoomIds, I.Set(action.payload.roomIds));
            return { ...store, loadingByRoomIds };
        }
        case actionCreators.ensureRoomsLoadedSucceeded.type:
        case actionCreators.ensureRoomsLoadedFailed.type: {
            let { loadingByRoomIds } = store;
            loadingByRoomIds = loadingByRoomIds.subtract(action.payload.roomIds);
            return { ...store, loadingByRoomIds };
        }
        //
        case actionCreators.selectRosterView.type: {
            const activeRosterView = action.payload.elementId;
            const activeSettingsView =
                activeRosterView === "settings" ? "index" : store.activeSettingsView;
            return { ...store, activeRosterView, activeSettingsView };
        }
        case actionCreators.selectSettingsView.type: {
            return { ...store, activeSettingsView: action.payload.viewId };
        }
        //
        default: {
            return store;
        }
    }
}

export type OuterStore = { roster: Store };

export function getRooms(store: OuterStore) {
    return store.roster.rooms;
}

export function getSearchResults(store: OuterStore) {
    return store.roster.searchResults;
}

export function getRoom(store: OuterStore, roomId: string) {
    return store.roster.rooms.get(roomId);
}

export function getRoomType(store: OuterStore, roomId: string) {
    const room = store.roster.rooms.get(roomId);
    if (room) {
        return room.type;
    }
    return;
}

export function getRoomName(store: OuterStore, roomId: string) {
    const room = getRoom(store, roomId);
    return (room && room.name) || undefined;
}

export function findRoomIdByUri(store: OuterStore, uri: string) {
    return store.roster.rooms.findKey(info => (info && info.sip_uri === uri) || false);
}

export function getMonologRoomId(store: OuterStore) {
    return store.roster.monologRoomId;
}

export const selectMonologRoom = createSelector(
    getMonologRoomId,
    getRooms,
    (monologRoomId, rooms) => {
        const room = monologRoomId && rooms.get(monologRoomId);
        return room || undefined;
    }
);

export function getMonologUserId(store: OuterStore) {
    const { monologRoomId, rooms } = store.roster;
    const room = monologRoomId && rooms.get(monologRoomId);
    return (room && room.userId) || undefined;
}

export function getUserNamesByUserIds(store: OuterStore, userIds: I.Set<string>) {
    const { rooms } = store.roster;
    let out = userIds.toMap();
    rooms.forEach(({ userId, name }) => {
        if (userId && name && userIds.contains(userId)) {
            out = out.set(userId, name);
        }
    });
    return out;
}

export function checkUsersHaveName(store: OuterStore, userIds: string[]) {
    const { rooms } = store.roster;
    const out = userIds.slice();
    rooms.forEach(({ userId, name }) => {
        if (userId && name) {
            const index = out.indexOf(userId);
            if (index !== -1) {
                out.splice(index, 1);
            }
        }
    });
    return out;
}

export function getUsersToLoad(store: OuterStore, userIds: string[]) {
    const { loadingByUserIds } = store.roster;
    const out = userIds.filter(x => !loadingByUserIds.contains(x));
    return out;
}

export function getRoomsToLoad(store: OuterStore, roomIds: string[]) {
    const { loadingByRoomIds } = store.roster;
    const out = roomIds.filter(x => !loadingByRoomIds.contains(x));
    return out;
}

function hasBadge(x: DisplayableRoom): x is DisplayableRoom & { badge: Data.Badge } {
    return x.badge ? x.badge.count > 0 : false;
}

function isFollowing(x: DisplayableRoom) {
    return x.type === "forum" && x.forum_level === "follow";
}

export function isPrivateForum(room: DisplayableRoom) {
    return (
        room.forum_access &&
        (room.forum_access.full === undefined ||
            !room.forum_access.full.find(x => x === "everyone"))
    );
}

export const getRoster = createSelector(
    getRooms,
    rooms => {
        return rooms.filter(isDisplayableRoom);
    }
);

const getRosterWithBadgesCache = shallowSameOrderedMap<
    string,
    DisplayableRoom & { badge: Data.Badge }
>();
export const getRosterWithBadges = createSelector(
    getRoster,
    rooms => {
        return getRosterWithBadgesCache(rooms.filter(hasBadge));
    }
);

export const selectRosterCounters = createSelector(
    getRosterWithBadges,
    rooms => {
        let allCounter = false;
        let roomsCounter = false;
        let dialogsCounter = false;
        let contactsCounter = false;
        rooms.some(room => {
            if (room.badge.count) {
                allCounter = true;
                roomsCounter = roomsCounter || room.type === "forum";
                dialogsCounter = dialogsCounter || room.type === "dialog";
                contactsCounter = contactsCounter || room.type === "contact";
            }
            // no need to traverse once all of them are true
            return roomsCounter && dialogsCounter && contactsCounter;
        });
        return { allCounter, roomsCounter, dialogsCounter, contactsCounter };
    }
);

export const getRosterWithFollowing = createSelector(
    getRoster,
    rooms => {
        return rooms.filter(isFollowing);
    }
);

// to make sure that value returned from selector is comparable by reference
let badgeCache = {
    count: 0,
    hasMentioned: false,
    badgeText: "",
};

export const selectElectronBadge = createSelector(
    getRosterWithBadges,
    rooms => {
        let count = 0;
        let hasMentioned = false;
        rooms.forEach(({ badge }) => {
            count = count + badge.count;
            hasMentioned = badge.mentioned || hasMentioned;
        });
        let badgeText = "";
        if (count > 99) {
            badgeText = "99+";
        } else if (count > 0) {
            badgeText = `${count}`;
        }
        if (hasMentioned && badgeText) {
            badgeText = `[${badgeText}]`;
        }
        badgeCache = produce(badgeCache, draft => {
            draft.count = count;
            draft.hasMentioned = hasMentioned;
            draft.badgeText = badgeText;
        });
        return badgeCache;
    }
);

export function isDisplayableRoom(room: RoomInfo): room is DisplayableRoom {
    return room.id !== undefined && room.name !== undefined && room.createdTs !== undefined;
}

export function isDisplayableUser(room: DisplayableRoom): room is DisplayableUser {
    return (
        (room.type === "dialog" || room.type === "monolog") &&
        (room.dialog_id !== undefined || room.monolog_id !== undefined) &&
        room.userId !== undefined
    );
}

export const selectUserRoster = createSelector(
    (s: RootState) => getOpenRoomIds(s),
    getRoster,
    (openRooms, rooms) => rooms.filter(x => isInsideUserRoster(x) || openRooms.has(x.id))
);

export const selectSearchResults = createSelector(
    getSearchResults,
    getRooms,
    (searchResults, rooms) => {
        return searchResults.filter(isDisplayableRoom).map(room => {
            // API don't return forum_level and badge information, so we're getting it from user's roster
            const inRoster = room.id && rooms.get(room.id);
            if (inRoster) {
                return {
                    ...room,
                    forum_level: inRoster.forum_level || "watch",
                    badge: inRoster.badge,
                };
            }
            return room;
        });
    }
);

const selectRosterItemsCache = shallowSameOrderedMap<string, DisplayableRoom>();

export const selectRosterItems = createSelector(
    (store: RootState, { itemType }: { itemType: "all" | "forum" | "dialog" | "contact" }) =>
        itemType,
    selectUserRoster,
    selectSearchResults,
    getRosterSearchTerm,
    (itemType, roster, searchResults, rosterSearchTerm) =>
        selectRosterItemsCache(
            (rosterSearchTerm.length === 0 ? roster : searchResults).filter(
                room =>
                    room.name &&
                    room.type !== "monolog" &&
                    (itemType === "all" || room.type === itemType)
            )
        )
);

export const getDialogsWithMonolog = createSelector(
    getRoster,
    rooms => {
        return rooms.filter(isDisplayableUser);
    }
);

export const getDialogsWithoutMonolog = createSelector(
    getDialogsWithMonolog,
    rooms => rooms.filter((x): x is DisplayableDialog => x.type === "dialog")
);

export function getMentionsMultiMap(store: OuterStore) {
    return store.roster.mentionsMultiMap;
}

export const selectDialogsWithNames = createSelector(
    getRooms,
    rooms => {
        return rooms.mapEntries(([id, room]) => {
            if (room.userId && room.name) {
                return [room.userId, room.name];
            }
            // delete rooms with no user or no name
            // SMELL: returning undefined is undocumented in immutable
            return (undefined as any) as [string, string];
        });
    }
);

export function getIsRosterLoaded(store: OuterStore) {
    return store.roster.isRosterLoaded;
}

export function getForumLevel(store: OuterStore, roomId: string) {
    const room = store.roster.rooms.get(roomId);
    return !room ? undefined : room.forum_level || "watch";
}

// TD:LOW selectPrivateRoomMembers should return mention info for particular room based on what users are searched for and not be closure
export const selectPrivateRoomMembers = createSelector(
    getDialogsWithMonolog,
    dialogsWithMonolog => (ids: I.OrderedSet<string>) =>
        ids.map(id => {
            const room = dialogsWithMonolog.find(room => room.userId === id);
            if (room) {
                return {
                    id,
                    avatar: room.avatarUrl,
                    name: room.display_name,
                    disabled: room.dialog_disabled === true,
                };
            } else {
                return { id, avatar: undefined, name: "UNKNOWN", disabled: false };
            }
        })
);

export function getActiveRosterView(store: OuterStore) {
    return store.roster.activeRosterView;
}

export function getActiveSettingsView(store: OuterStore) {
    return store.roster.activeSettingsView;
}
